import { useEffect, useRef, type KeyboardEvent } from 'react'
import { dateFr } from './projetsNatures'
import type { ResidenceCrous as Residence } from './crous'
import { lienCrous } from './crous'
import { cleAdresse, cleTexte, contactsCrous, presentationCrous, texteCrous, type RubriqueCrous } from './crousPresentation'
import './crous.css'

const LIENS: Record<string, string> = {
  site: 'Voir la résidence sur le site du CROUS', reservation: 'Réserver un logement',
  visite_virtuelle: 'Visiter la résidence en ligne', rendez_vous: 'Prendre rendez-vous',
  assistance: 'Demander une assistance', crous_and_go: 'CROUS and Go',
  album: 'Voir l’album sur le site du CROUS',
}

function fermerAvecEchap(e: KeyboardEvent<HTMLDetailsElement>) {
  if (e.key !== 'Escape') return
  e.currentTarget.open = false
  e.currentTarget.querySelector('summary')?.focus()
  e.stopPropagation()
}

function Rubriques({ rubriques, listes = false }: { rubriques: RubriqueCrous[]; listes?: boolean }) {
  return rubriques.map((r, i) => {
    // L’introduction précède la liste, dont le début est un type explicitement publié.
    const debutListe = listes && /^Types de logements?/i.test(r.titre) ? r.lignes.findIndex(t => /^T\d+\b/i.test(t)) : -1
    const lignes = []
    for (let j = 0; j < r.lignes.length;) {
      if (debutListe >= 0 && j >= debutListe) {
        const debut = j
        j = r.lignes.length
        lignes.push(<ul key={debut}>{r.lignes.slice(debut, j).map((t, k) => <li key={k}>{t}</li>)}</ul>)
      } else {
        lignes.push(<p className="crous-texte" key={j}>{r.lignes[j]}</p>)
        j++
      }
    }
    return <div key={i}>{r.titre && <h5>{r.titre}</h5>}{lignes}</div>
  })
}

// `adresseAffichee` : le résumé de la liste montre déjà l'adresse ; la fenêtre de la carte, non.
export function DetailResidenceCrous({ residence: r, adresseAffichee = false }: { residence: Residence; adresseAffichee?: boolean }) {
  const f = r.fiche
  const { complet, logements, acces } = presentationCrous(f)
  const contacts = contactsCrous(f, r.adresse)
  const groupes = contacts.filter((c, i) => c.type !== 'courriel' || contacts.findIndex(v => v.type === 'courriel') === i)
    .map(c => ({ libelle: c.libelle, contacts: c.type === 'courriel' ? contacts.filter(v => v.type === 'courriel') : [c] }))
  const extrait = texteCrous(r.capacite_extrait ?? '')
  const citation = extrait && !cleTexte(complet.flatMap(b => b.lignes).join('\n')).includes(cleTexte(extrait)) ? extrait : null
  const zone = f?.zone && !cleAdresse(r.adresse).includes(cleAdresse(f.zone)) ? f.zone : null
  const identite = [adresseAffichee ? null : r.adresse, zone].filter(Boolean).join(' · ')
  const liens = Object.entries(LIENS).flatMap(([cle, label]) => {
    const url = lienCrous(f?.liens?.[cle])
    return url ? [{ cle, label, url }] : []
  })
  return <div className="crous-detail">
    <div className="crous-colonnes">
      <div>
        {identite && <p className="crous-identite">{texteCrous(identite)}</p>}
        {!!f?.services?.length && <>
          <ul className="crous-puces" aria-label="Services">{f.services.map(s => <li key={s}>{texteCrous(s)}</li>)}</ul>
          {f.services_source === 'services' && <small>Libellés issus du texte libre du CROUS.</small>}
        </>}
        {(logements.length > 0 || citation) && <section><h4>Logements</h4>
          {logements.length > 0 && <blockquote className="crous-citation">
            <Rubriques rubriques={logements} listes />
          </blockquote>}
          {citation && <p className="crous-extrait">Selon le CROUS : « {citation} »</p>}
        </section>}
        {acces.length > 0 && <section><h4>Accès</h4>
          <blockquote className="crous-citation"><Rubriques rubriques={acces} /></blockquote>
        </section>}
      </div>
      {(contacts.length > 0 || f?.horaires || liens.length > 0) && <div className="crous-pratique">
        {(contacts.length > 0 || f?.horaires) && <section><h4>Contacter le CROUS</h4>
          <dl className="crous-contact">{groupes.map((g, i) => <div key={i}><dt>{g.libelle}</dt>{g.contacts.map((c, j) => {
            const href = c.type === 'telephone' && /^[+\d().\s-]+$/.test(c.valeur) ? `tel:${c.valeur.replace(/[^+\d]/g, '')}`
              : c.type === 'courriel' && /^[^\s@]+@[^\s@]+$/.test(c.valeur) ? `mailto:${encodeURIComponent(c.valeur).replace(/%40/g, '@')}` : null
            return <dd key={j} className="crous-texte">{href ? <a href={href}>{c.valeur}</a> : c.valeur}</dd>
          })}</div>)}</dl>
          {f?.horaires && <div><h5>Horaires</h5><p className="crous-texte">{texteCrous(f.horaires)}</p></div>}
        </section>}
        {liens.length > 0 && <section><h4>Liens officiels</h4><ul className="crous-liens">
          {liens.map(l => <li key={l.cle}><a href={l.url} target="_blank" rel="noopener noreferrer">{l.label}</a></li>)}
        </ul></section>}
      </div>}
    </div>
    {complet.length > 0 && <details className="crous-complet" onKeyDown={fermerAvecEchap}>
      <summary>Texte complet publié par le CROUS</summary>
      <blockquote className="crous-citation"><Rubriques rubriques={complet} /></blockquote>
    </details>}
    <footer>Source : CNOUS — réseau des CROUS · Licence Ouverte (Etalab){r.date_traitement && ` · traitement du portail ${dateFr(r.date_traitement)}`}
      <details onKeyDown={fermerAvecEchap}>
        <summary>Détails techniques</summary>
        {r.latitude != null && r.longitude != null && <p>Coordonnées : {r.latitude}, {r.longitude} (latitude, longitude).</p>}
        {r.date_metadata && <p>Métadonnées éditoriales : {dateFr(r.date_metadata)}.</p>}
        <p>La date de traitement ne date pas un recensement du parc.</p>
      </details>
    </footer>
  </div>
}

export default function ResidenceCrous({ residence: r, ouvrir = false }: { residence: Residence; ouvrir?: boolean | number }) {
  const details = useRef<HTMLDetailsElement>(null)
  const summary = useRef<HTMLElement>(null)
  useEffect(() => {
    if (ouvrir && details.current) { details.current.open = true; summary.current?.focus() }
  }, [ouvrir])
  return <details className="crous-residence" ref={details} onKeyDown={fermerAvecEchap}>
    <summary ref={summary}>
      <strong>{texteCrous(r.nom)}</strong>
      {!!r.fiche?.services?.length && <span className="crous-services">{texteCrous(r.fiche.services.slice(0, 3).join(' · '))}{r.fiche.services.length > 3 ? '…' : ''}</span>}
      {r.adresse && <small>{texteCrous(r.adresse)}</small>}
    </summary>
    <DetailResidenceCrous residence={r} adresseAffichee={!!r.adresse} />
  </details>
}
